import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, DollarSign, FileText, Loader, CheckCircle, AlertCircle,
  BarChart3, PieChart as PieChartIcon, Download, Mail, Calendar,
  TrendingDown, Package, Eye, Trash2, FlaskConical
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api, { getErrorMessage } from '../utils/api';
import { CHART_COLORS } from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import ReportHistoryModal from '../components/ReportHistoryModal';
import YearMonthPicker from '../components/YearMonthPicker';

export default function ExpenseManager() {
  const { user: currentUser } = useAuth()
  const isAdmin = currentUser?.role === 'admin'
  const navigate = useNavigate()
  
  // 客戶與預算狀態
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [activeBudget, setActiveBudget] = useState(null);
  
  // 步驟控制
  const [currentStep, setCurrentStep] = useState(1); // 1: 設定預算, 2: 上傳報表, 3: 查看明細
  
  // 預算表單
  const [budgetForm, setBudgetForm] = useState({
    initial_amount: '',
    discount_rate: '0',
    tax_rate: '0.05',
    currency: 'TWD',
    period_start: new Date().toISOString().split('T')[0],
    period_end: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    order_number: '',
    other_number: '',
    remark: '',
  });
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [isEditingDiscount, setIsEditingDiscount] = useState(false);
  const [isEditingPeriod, setIsEditingPeriod] = useState(false);
  const [periodForm, setPeriodForm] = useState({ period_start: '', period_end: '' });
  
  // 上傳狀態
  const [files, setFiles] = useState([]);  // 改為多檔案陣列
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadedReports, setUploadedReports] = useState([]);  // 批量上傳結果
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, currentFile: '' });  // 批量進度
  const [isDragging, setIsDragging] = useState(false);  // 拖曳狀態
  
  // 明細數據
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState('');
  const [expenseItems, setExpenseItems] = useState([]);
  const [expenseItemsReportId, setExpenseItemsReportId] = useState(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  // Summary 只在報表 metadata 與該報表明細完成同步後顯示，避免切換期間讀到上一張報表。
  const [summarySource, setSummarySource] = useState(null);
  
  // 用於追蹤當前請求的報表 ID，防止競態條件
  const currentRequestRef = useRef(null);
  const abortControllerRef = useRef(null);
  
  // 訊息提示
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualReportForm, setManualReportForm] = useState({
    billing_start: new Date().toISOString().split('T')[0],
    billing_end: new Date().toISOString().split('T')[0],
    total_amount: '',
    remark: '',
  });

  // 計算當前選擇的客戶是否有編輯權限（管理員、建立者、或被授權的編輯者）
  const selectedCustomerObj = customers.find(c => String(c.id) === String(selectedCustomer));
  const canEdit = isAdmin || (selectedCustomerObj?.created_by_user_id === currentUser?.id) || (selectedCustomerObj?.editor_ids?.includes(currentUser?.id));

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      checkCustomerStatus();
    }
  }, [selectedCustomer]);

  useEffect(() => () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    currentRequestRef.current = null;
  }, []);

  const cancelPendingReportRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    currentRequestRef.current = null;
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers?status=active');
      const customerData = response.data.data || response.data; // 處理 {data: [...]} 和 [...] 兩種格式
      setCustomers(customerData);
      if (customerData.length > 0) {
        setSelectedCustomer(customerData[0].id);
      }
    } catch (error) {
      console.error('獲取客戶列表失敗:', error);
    }
  };

  const checkCustomerStatus = async (preferredReportId = null) => {
    try {
      setLoading(true);
      cancelPendingReportRequest();
      setSummarySource(null);
      setExpenseItems([]);
      setExpenseItemsReportId(null);
      // 檢查是否有活動預算
      const budgetRes = await api.get(`/budget/customer/${selectedCustomer}/active`);
      const budgetData = budgetRes.data;
      
      // 檢查是否有預算
      if (budgetData.data) {
        setActiveBudget(budgetData.data);
      } else {
        // 沒有活動預算
        setActiveBudget(null);
        setMessage({ 
          type: 'info', 
          text: `ℹ️ ${budgetData.suggestion || '此客戶尚未設定預算,請先建立預算'}` 
        });
        setCurrentStep(1);
        setLoading(false);
        return;
      }
      
      // 如果有預算,載入報表列表
      const reportsRes = await api.get(`/expense/customer/${selectedCustomer}/reports`);
      const reportList = reportsRes.data.reports || reportsRes.data || [];
      setReports(reportList);
      
      // 自動進入步驟 2 (上傳報表)
      setCurrentStep(2);
      
      if (reportList.length > 0) {
        // 檢查當前選擇的報表是否還存在於列表中
        const currentExists = selectedReport && reportList.some(r => r.id === selectedReport || r.id === parseInt(selectedReport));
        
        const preferredExists = preferredReportId && reportList.some(
          r => r.id === preferredReportId || r.id === parseInt(preferredReportId)
        );
        const reportId = preferredExists
          ? preferredReportId
          : currentExists
            ? selectedReport
            : reportList[0].id;
        console.log('[checkCustomerStatus] 切換至報表:', reportId);
        await switchReport(reportId, reportList);
        setCurrentStep(3);
      } else {
        setSelectedReport(null);
        setCurrentStep(2);
      }
    } catch (error) {
      // 處理錯誤
      console.error('檢查客戶狀態失敗:', error);
      setMessage({ 
        type: 'error', 
        text: getErrorMessage(error, '無法載入客戶資料')
      });
      
      // 沒有預算,停留在步驟 1
      setActiveBudget(null);
      setCurrentStep(1);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBudget = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const otherNumber = (budgetForm.other_number || '').trim();
      if (!otherNumber) {
        setMessage({ type: 'error', text: '❌ 其他單號為必填' });
        setLoading(false);
        return;
      }
      const parsedTaxRate = parseFloat(budgetForm.tax_rate);
      const taxRate = Number.isNaN(parsedTaxRate) ? 0.05 : parsedTaxRate;
      if (isEditingBudget && activeBudget) {
        // 更新現有預算 - 輸入的是增減金額
        const adjustAmount = parseFloat(budgetForm.initial_amount);
        const newInitialAmount = activeBudget.initial_amount + adjustAmount;
        const newRemainingAmount = activeBudget.remaining_amount + adjustAmount;
        
        if (newInitialAmount <= 0) {
          setMessage({ type: 'error', text: '❌ 調整後的初始金額不能小於等於 0' });
          setLoading(false);
          return;
        }
        if (adjustAmount < 0 && newRemainingAmount < 0) {
          setMessage({ type: 'error', text: '❌ 減少後的剩餘金額不能小於 0' });
          setLoading(false);
          return;
        }
        
        await api.put(`/budget/${activeBudget.id}/update`, {
          initial_amount: newInitialAmount,
          remaining_amount: newRemainingAmount,
          discount_rate: parseFloat(budgetForm.discount_rate) || 0,
          tax_rate: taxRate,
          currency: budgetForm.currency,
          period_start: budgetForm.period_start,
          period_end: budgetForm.period_end,
          order_number: budgetForm.order_number || undefined,
          other_number: otherNumber,
          remark: (budgetForm.remark || '').trim() || undefined,
        });
        
        const action = adjustAmount > 0 ? '增加' : '減少';
        const absAmount = Math.abs(adjustAmount);
        setMessage({ type: 'success', text: `✅ 預算${action} $${absAmount.toLocaleString()} 成功!` });
        setIsEditingBudget(false);
      } else {
        // 初始化新預算
        await api.post('/budget/init', {
          customer_id: selectedCustomer,
          ...budgetForm,
          discount_rate: parseFloat(budgetForm.discount_rate) || 0,
          tax_rate: taxRate,
          order_number: budgetForm.order_number || undefined,
          other_number: otherNumber,
          remark: (budgetForm.remark || '').trim() || undefined,
        });
        setMessage({ type: 'success', text: '✅ 預算設定成功!' });
      }
      await checkCustomerStatus();
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '操作失敗') });
    } finally {
      setLoading(false);
    }
  };

  const handleEditBudget = () => {
    setBudgetForm({
      initial_amount: '0', // 改為輸入增減金額,預設為0
      discount_rate: String(activeBudget.discount_rate || 0),
      tax_rate: String(activeBudget.tax_rate ?? 0.05),
      currency: activeBudget.currency,
      period_start: activeBudget.period_start || new Date().toISOString().split('T')[0],
      period_end: activeBudget.period_end || new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      order_number: '',
      other_number: '',
      remark: '',
    });
    setIsEditingBudget(true);
  };

  const handleEditDiscount = () => {
    setBudgetForm({
      ...budgetForm,
      discount_rate: String(activeBudget.discount_rate || 0),
      tax_rate: String(activeBudget.tax_rate ?? 0.05)
    });
    setIsEditingDiscount(true);
  };

  const handleSaveDiscount = async () => {
    try {
      setLoading(true);
      const discountRate = parseFloat(budgetForm.discount_rate) || 0;
      
      if (discountRate < 0 || discountRate > 1) {
        setMessage({ type: 'error', text: '❌ 折扣率必須在 0-1 之間，例如 0.8 表示 8 折' });
        setLoading(false);
        return;
      }
      
      const parsedTaxRate = parseFloat(budgetForm.tax_rate);
      const taxRate = Number.isNaN(parsedTaxRate) ? 0.05 : parsedTaxRate;
      
      if (taxRate < 0 || taxRate > 1) {
        setMessage({ type: 'error', text: '❌ 稅率必須在 0-1 之間，例如 0.05 表示 5%' });
        setLoading(false);
        return;
      }
      
      await api.put(`/budget/${activeBudget.id}/update`, {
        discount_rate: discountRate,
        tax_rate: taxRate
      });
      
      setMessage({ type: 'success', text: `✅ 折扣率 ${discountRate} (${(discountRate * 10).toFixed(1)} 折)、稅率 ${(taxRate * 100).toFixed(0)}% 已儲存` });
      setIsEditingDiscount(false);
      await checkCustomerStatus();
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '更新折扣失敗') });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingBudget(false);
    setIsEditingDiscount(false);
    setIsEditingPeriod(false);
    setBudgetForm({
      initial_amount: '',
      discount_rate: '0',
      tax_rate: '0.05',
      currency: 'TWD',
      period_start: new Date().toISOString().split('T')[0],
      period_end: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
      order_number: '',
      other_number: '',
      remark: '',
    });
    setPeriodForm({ period_start: '', period_end: '' });
  };

  const handleEditPeriod = () => {
    setPeriodForm({
      period_start: activeBudget.period_start ? activeBudget.period_start.split('T')[0] : '',
      period_end: activeBudget.period_end ? activeBudget.period_end.split('T')[0] : ''
    });
    setIsEditingPeriod(true);
  };

  const handleSavePeriod = async () => {
    try {
      setLoading(true);
      
      if (!periodForm.period_start || !periodForm.period_end) {
        setMessage({ type: 'error', text: '❌ 請填寫開始和結束日期' });
        setLoading(false);
        return;
      }
      
      if (new Date(periodForm.period_start) > new Date(periodForm.period_end)) {
        setMessage({ type: 'error', text: '❌ 開始日期不能晚於結束日期' });
        setLoading(false);
        return;
      }
      
      await api.put(`/budget/${activeBudget.id}/update`, {
        period_start: periodForm.period_start,
        period_end: periodForm.period_end
      });
      
      setMessage({ type: 'success', text: '✅ 預算期間已更新' });
      setIsEditingPeriod(false);
      await checkCustomerStatus();
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '更新期間失敗') });
    } finally {
      setLoading(false);
    }
  };

  const handleResetBudget = async () => {
    if (!window.confirm('⚠️ 確定要將預算歸零重設嗎?\n這將會:\n- 取消當前預算\n- 清除該預算的交易紀錄\n- 清除該預算的費用報表與明細\n- 重新開始設定預算')) {
      return;
    }

    try {
      setLoading(true);
      // 取消當前預算；後端會保留 Audit Log 並移除交易紀錄。
      await api.put(`/budget/${activeBudget.id}/update`, {
        status: 'cancelled'
      });
      
      // 重置狀態
      setActiveBudget(null);
      setReports([]);
      setExpenseItems([]);
      setCurrentStep(1);
      setIsEditingBudget(false);
      setBudgetForm({
        initial_amount: '',
        discount_rate: '0',
        currency: 'TWD',
        period_start: new Date().toISOString().split('T')[0],
        period_end: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        order_number: '',
        other_number: '',
        remark: '',
      });
      
      setMessage({ type: 'success', text: '✅ 預算已取消並清除交易紀錄，可以重新設定！' });
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '重設失敗') });
    } finally {
      setLoading(false);
    }
  };

  // 驗證並設定檔案（點選和拖曳共用）
  const processFiles = (selectedFiles) => {
    if (selectedFiles.length > 0) {
      const allowedTypes = ['application/vnd.ms-excel', 
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      
      const validFiles = selectedFiles.filter(f => 
        allowedTypes.includes(f.type) || f.name.endsWith('.xlsx') || f.name.endsWith('.xls')
      );
      
      if (validFiles.length !== selectedFiles.length) {
        setMessage({ type: 'warning', text: `⚠️ 已過濾 ${selectedFiles.length - validFiles.length} 個不支援的檔案` });
      }
      
      if (validFiles.length > 0) {
        // 防呆：檢查是否與已上傳報表同名
        const existingNames = reports.map(r => r.file_name);
        const duplicates = validFiles.filter(f => existingNames.includes(f.name));
        
        if (duplicates.length > 0) {
          const dupNames = duplicates.map(f => f.name).join('、');
          setMessage({ type: 'error', text: `❌ 以下報表已存在，請勿重複上傳：${dupNames}\n如需重新上傳，請先從歷史報表中刪除舊報表。` });
          return;
        }
        
        setFiles(validFiles);
        setMessage({ type: 'info', text: `📁 已選擇 ${validFiles.length} 個檔案` });
      } else {
        setMessage({ type: 'error', text: '❌ 只支援 Excel 檔案 (.xlsx, .xls)' });
      }
    }
  };

  const handleFileChange = (e) => {
    processFiles(Array.from(e.target.files));
  };

  // 拖曳事件處理
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (canEdit) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!canEdit) return;
    const droppedFiles = Array.from(e.dataTransfer.files);
    processFiles(droppedFiles);
  };

  const handleUploadAndAnalyze = async () => {
    if (files.length === 0) {
      setMessage({ type: 'error', text: '❌ 請先選擇檔案' });
      return;
    }

    if (!activeBudget) {
      setMessage({ type: 'error', text: '❌ 請先設定預算' });
      return;
    }

    try {
      // 步驟 1: 批量上傳檔案
      setUploading(true);
      setBatchProgress({ current: 0, total: files.length, currentFile: '準備上傳...' });
      setMessage({ type: 'info', text: `⏳ 正在上傳 ${files.length} 個檔案...` });
      
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      formData.append('customer_id', selectedCustomer);
      formData.append('budget_id', activeBudget.id);

      const uploadRes = await api.post('/expense/upload-batch', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const uploadResults = uploadRes.data.results || [];
      const successUploads = uploadResults.filter(r => r.success);
      
      if (successUploads.length === 0) {
        throw new Error('所有檔案上傳失敗');
      }
      
      setUploadedReports(successUploads);
      setUploading(false);
      setMessage({ 
        type: 'success', 
        text: `✅ 上傳完成：${uploadRes.data.success_count} 成功，${uploadRes.data.fail_count} 失敗` 
      });

      // 步驟 2: 批量分析 (使用 SSE 即時更新進度)
      setAnalyzing(true);
      const reportIds = successUploads.map(r => r.report.id);
      setBatchProgress({ current: 0, total: reportIds.length, currentFile: '開始分析...' });
      setMessage({ type: 'info', text: `🤖 正在分析 ${reportIds.length} 個報表...` });
      
      // 使用 fetch + ReadableStream 接收 SSE
      const analyzeResponse = await fetch('/api/expense/analyze-batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ report_ids: reportIds }),
        credentials: 'include'
      });
      
      const reader = analyzeResponse.body.getReader();
      const decoder = new TextDecoder();
      let analyzeResult = null;
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'progress' || data.type === 'item_complete') {
                // 更新進度條
                setBatchProgress({
                  current: data.current,
                  total: data.total,
                  currentFile: data.currentFile || `已完成 ${data.current} / ${data.total}`
                });
              } else if (data.type === 'complete') {
                // 分析完成
                analyzeResult = data;
              }
            } catch (e) {
              // 忽略解析錯誤
            }
          }
        }
      }
      
      setAnalyzing(false);
      setBatchProgress({ current: 0, total: 0, currentFile: '' });
      
      if (analyzeResult) {
        const successMsg = `✅ 分析完成：${analyzeResult.success_count} 成功，${analyzeResult.fail_count} 失敗`;
        const totalAmount = analyzeResult.total_amount || 0;
        const discountedAmount = analyzeResult.total_discounted_amount || totalAmount;
        const amountWithTax = analyzeResult.total_amount_with_tax || discountedAmount;
        const hasDiscount = discountedAmount < totalAmount;
        const hasTax = amountWithTax > discountedAmount;
        
        let messageText = `${successMsg}\n💰 原始金額(未稅): $${totalAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}`;
        if (hasDiscount) {
          messageText += `\n🏷️ 折扣後(未稅): $${discountedAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}`;
        }
        if (hasTax) {
          messageText += `\n🧾 含稅扣款: $${amountWithTax.toLocaleString(undefined, {minimumFractionDigits: 2})}`;
        }
        
        setMessage({ 
          type: 'success', 
          text: messageText
        });
        
        // 如果有預算警告
        if (analyzeResult.budget_alert) {
          const alert = analyzeResult.budget_alert;
          const level = alert.level === 'critical' ? 'error' : 'warning';
          setMessage({ 
            type: level, 
            text: `⚠️ 預算使用率已達 ${alert.usage_percentage.toFixed(1)}%` 
          });
        }
        
        // 選擇最後一個成功分析的報表
        const successResults = analyzeResult.results?.filter(r => r.success) || [];
        if (successResults.length > 0) {
          const lastSuccessReportId = successResults[successResults.length - 1].report_id;
          console.log('[批量分析完成] 選擇最後成功的報表:', lastSuccessReportId);
          await checkCustomerStatus(lastSuccessReportId);
        } else {
          await checkCustomerStatus();
        }
      } else {
        await checkCustomerStatus();
      }
      
      setFiles([]);
      setUploadedReports([]);
      
    } catch (error) {
      setUploading(false);
      setAnalyzing(false);
      setMessage({ type: 'error', text: getErrorMessage(error, '處理失敗') });
      console.error('上傳分析錯誤:', error);
    }
  };

  const handleClearReports = async () => {
    if (!selectedCustomer) return;
    
    const confirmed = window.confirm('⚠️ 確定要清除此客戶的所有歷史報表嗎？\n\n此操作將：\n• 刪除所有上傳的報表\n• 刪除所有費用明細\n• 還原已扣款金額至預算\n• 移除相關交易紀錄\n\n此操作無法復原！');
    
    if (!confirmed) return;
    
    try {
      setLoading(true);
      const res = await api.delete(`/expense/customer/${selectedCustomer}/reports`);
      
      let msg = `✅ 已清除 ${res.data.deleted_count} 筆報表`;
      if (res.data.refunded_amount) {
        msg += `，已還原 $${res.data.refunded_amount.toLocaleString()} 至預算`;
      }
      setMessage({ type: 'success', text: msg });
      
      // 重新載入數據（含預算）
      setReports([]);
      setExpenseItems([]);
      setExpenseItemsReportId(null);
      setSummarySource(null);
      setSelectedReport(null);
      setCurrentStep(2);
      await checkCustomerStatus();
      
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '清除失敗') });
      console.error('清除報表錯誤:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateManualReport = async (e) => {
    e.preventDefault();

    if (!activeBudget) {
      setMessage({ type: 'error', text: '❌ 請先設定預算' });
      return;
    }

    if (!manualReportForm.billing_start || !manualReportForm.billing_end || !manualReportForm.total_amount) {
      setMessage({ type: 'error', text: '❌ 請完整填寫手動報表欄位' });
      return;
    }

    if (new Date(manualReportForm.billing_start) > new Date(manualReportForm.billing_end)) {
      setMessage({ type: 'error', text: '❌ 起始日期不可晚於結束日期' });
      return;
    }

    const totalAmount = parseFloat(manualReportForm.total_amount);
    if (Number.isNaN(totalAmount) || totalAmount <= 0) {
      setMessage({ type: 'error', text: '❌ 原始總金額需大於 0' });
      return;
    }

    try {
      setManualSubmitting(true);

      const response = await api.post('/expense/manual-report', {
        customer_id: Number(selectedCustomer),
        budget_id: activeBudget.id,
        billing_start: manualReportForm.billing_start,
        billing_end: manualReportForm.billing_end,
        total_amount: totalAmount,
        remark: (manualReportForm.remark || '').trim() || undefined,
      });

      const report = response.data?.report;
      const deducted = report?.amount_with_tax ?? report?.discounted_amount ?? report?.total_amount ?? totalAmount;
      setMessage({ type: 'success', text: `✅ 手動報表已建立，已扣款 $${Number(deducted || 0).toLocaleString()}` });

      setManualReportForm({
        ...manualReportForm,
        total_amount: '',
        remark: '',
      });

      await checkCustomerStatus(report?.id);
    } catch (error) {
      setMessage({ type: 'error', text: getErrorMessage(error, '手動建立報表失敗') });
    } finally {
      setManualSubmitting(false);
    }
  };

  const switchReport = async (reportId, reportList = reports) => {
    if (!reportId) return;
    cancelPendingReportRequest();
    setSelectedReport(reportId);
    setSummarySource(null);
    setExpenseItems([]);
    setExpenseItemsReportId(null);
    setCurrentStep(3);
    await fetchExpenseItems(reportId, reportList);
  };

  const fetchExpenseItems = async (reportId, reportList = reports) => {
    if (!reportId) {
      console.error('[fetchExpenseItems] reportId is missing');
      return;
    }

    const controller = new AbortController();
    
    // 記錄這次請求的 ID，用於檢查競態條件
    const requestId = reportId;
    currentRequestRef.current = requestId;
    abortControllerRef.current = controller;
    const reportMeta = reportList.find(r => r.id === reportId || r.id === parseInt(reportId));
    setSummarySource({ reportId, currentReport: reportMeta, itemsLoaded: false });
    
    try {
      setItemsLoading(true);
      // 先清空舊資料，避免顯示錯誤的報表內容
      setExpenseItems([]);
      
      console.log(`[fetchExpenseItems] 開始載入報表 ${reportId} 的費用明細...`);
      const response = await api.get(`/expense/${reportId}/items`, { signal: controller.signal });
      
      // 檢查是否仍然是當前選擇的報表（防止競態條件）
      if (currentRequestRef.current !== requestId) {
        console.log(`[fetchExpenseItems] 報表 ${reportId} 的請求已過期，忽略結果`);
        return;
      }
      
      const itemList = response.data.items || response.data || [];
      console.log(`[fetchExpenseItems] 報表 ${reportId} 載入完成，共 ${itemList.length} 項費用`);
      setExpenseItems(itemList);
      setExpenseItemsReportId(reportId);
      setSummarySource({ reportId, currentReport: reportMeta, itemsLoaded: true });
      
      if (itemList.length === 0) {
        if (reportMeta?.file_type === 'manual') {
          setMessage({ type: 'info', text: '此為手動輸入報表，不產生費用項目，僅顯示金額統計' });
        } else {
          setMessage({ type: 'info', text: '此報表尚未分析或無費用項目，請先重新分析' });
        }
      }
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
        console.log(`[fetchExpenseItems] 報表 ${reportId} 的請求已取消`);
        return;
      }

      // 檢查是否仍然是當前選擇的報表
      if (currentRequestRef.current !== requestId) {
        console.log(`[fetchExpenseItems] 報表 ${reportId} 的請求已過期，忽略錯誤`);
        return;
      }
      
      console.error('[fetchExpenseItems] 獲取費用明細失敗:', error);
      setExpenseItems([]);
      setMessage({ type: 'error', text: '無法載入費用明細' });
    } finally {
      // 只有當前請求才清除 loading 狀態
      if (currentRequestRef.current === requestId) {
        setItemsLoading(false);
        currentRequestRef.current = null;
      }
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  };

  const handleGenerateExcel = async () => {
    try {
      setLoading(true);
      const response = await api.post(
        `/expense/${selectedReport}/generate-excel`,
        {},
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expense-report-${selectedReport}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setMessage({ type: 'success', text: '✅ Excel 已下載' });
    } catch (error) {
      setMessage({ type: 'error', text: '❌ 生成失敗' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    try {
      setLoading(true);
      await api.post(`/expense/${selectedReport}/send-email`);
      setMessage({ type: 'success', text: '✅ 郵件已發送' });
    } catch (error) {
      setMessage({ type: 'error', text: '❌ 發送失敗' });
    } finally {
      setLoading(false);
    }
  };

  // 計算統計數據
  const calculateStats = () => {
    console.log('[calculateStats] expenseItems 數量:', expenseItems.length, 
      expenseItems.length > 0 ? `第一筆: ${expenseItems[0]?.service_name}` : '');
    if (expenseItems.length === 0) return null;
    
    // 用原始小數金額加總計算百分比，顯示用整數
    const rawTotal = expenseItems.reduce((sum, item) => sum + item.amount, 0);
    const totalAmount = Math.round(rawTotal);
    const categoryMap = {};
    
    expenseItems.forEach(item => {
      if (!categoryMap[item.category]) {
        categoryMap[item.category] = { total: 0, count: 0 };
      }
      categoryMap[item.category].total += item.amount;
      categoryMap[item.category].count += 1;
    });
    
    const categoryData = Object.entries(categoryMap).map(([name, data]) => ({
      name,
      value: data.total,
      count: data.count,
      percentage: ((data.total / rawTotal) * 100).toFixed(1)
    }));
    
    return {
      totalAmount,
      itemCount: expenseItems.length,
      categoryCount: Object.keys(categoryMap).length,
      categoryData
    };
  };

  const stats = calculateStats();
  const currentReport = reports.find(r => r.id === selectedReport || r.id === parseInt(selectedReport));
  const selectedReportView = summarySource && String(summarySource.reportId) === String(selectedReport)
    ? summarySource
    : null;
  const summaryReport = selectedReportView?.currentReport || currentReport;
  const isManualReport = summaryReport?.file_type === 'manual';
  const summaryReady = Boolean(
    selectedReportView?.itemsLoaded &&
    String(expenseItemsReportId) === String(selectedReport) &&
    summaryReport
  );
  const shouldShowSummaryCards = summaryReady && (expenseItems.length > 0 || isManualReport);
  const COLORS = ['#0ea5e9', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#6366f1'];

  const formatCurrency = (amount, currency = 'TWD') => {
    // 金額四捨五入到整數後顯示
    const roundedAmount = Math.round(amount);
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: currency || 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(roundedAmount);
  };

  const formatBillingMonth = (dateValue) => {
    if (!dateValue) return '';
    const match = String(dateValue).match(/^(\d{4})-(\d{2})/);
    if (!match) return '';
    return `${match[1]}年${match[2]}月`;
  };

  const formatBillingPeriod = (report) => {
    const startMonth = formatBillingMonth(report?.billing_start);
    const endMonth = formatBillingMonth(report?.billing_end);

    if (startMonth && endMonth) {
      return startMonth === endMonth ? startMonth : `${startMonth} - ${endMonth}`;
    }

    return startMonth || endMonth || '未取得帳期';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-100';
    if (confidence >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading && customers.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* 標題與客戶選擇 */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">💰 記帳系統</h1>
              <p className="text-gray-500 mt-1">簡單三步驟:選客戶 → 設預算/上傳 → 查看明細</p>
            </div>
            
            {/* 步驟指示器 */}
            <div className="flex items-center space-x-4">
              {[
                { num: 1, label: '設定預算', icon: DollarSign },
                { num: 2, label: '上傳報表', icon: Upload },
                { num: 3, label: '查看明細', icon: BarChart3 }
              ].map(({ num, label, icon: Icon }) => (
                <div key={num} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    currentStep >= num ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {currentStep > num ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    currentStep >= num ? 'text-gray-900' : 'text-gray-400'
                  }`}>{label}</span>
                  {num < 3 && <div className="w-8 h-0.5 bg-gray-300 mx-2" />}
                </div>
              ))}
            </div>
          </div>

          {/* 客戶選擇 */}
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">選擇客戶:</label>
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              className="flex-1 max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.customer_name || customer.name} - {customer.company || '個人'} - {customer.name}
                </option>
              ))}
            </select>
            
            {activeBudget && (
              <div className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">預算已設定</span>
              </div>
            )}
            
            {!canEdit && selectedCustomerObj && (
              <div className="flex items-center space-x-2 px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-yellow-600" />
                <span className="text-sm text-yellow-700">僅限檢視（此客戶由 {selectedCustomerObj.created_by || '其他人'} 建立）</span>
              </div>
            )}
          </div>
        </div>

        {/* 訊息提示 */}
        {message.text && (
          <div className={`p-4 rounded-lg flex items-center space-x-2 ${
            message.type === 'success' ? 'bg-green-100 text-green-800' :
            message.type === 'error' ? 'bg-red-100 text-red-800' :
            message.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            {message.type === 'success' && <CheckCircle className="w-5 h-5" />}
            {message.type === 'error' && <AlertCircle className="w-5 h-5" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* 主要內容區 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* 左側: 預算設定 (步驟 1) */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center space-x-2 mb-4">
              <div className="p-2 bg-blue-500 rounded-lg">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">設定預算</h2>
            </div>

            {activeBudget && !isEditingBudget && !isEditingDiscount ? (
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl text-white">
                  <p className="text-sm opacity-90 mb-1">目前預算</p>
                  <p className="text-3xl font-bold">{formatCurrency(activeBudget.remaining_amount, activeBudget.currency)}</p>
                  <div className="mt-3 pt-3 border-t border-white/20">
                    <div className="flex justify-between text-sm">
                      <span>總預算</span>
                      <span>{formatCurrency(activeBudget.initial_amount, activeBudget.currency)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span>使用率</span>
                      <span>{(() => {
                        const init = activeBudget.initial_amount || 0;
                        if (init > 0) return `${((1 - activeBudget.remaining_amount / init) * 100).toFixed(1)}%`;
                        if (activeBudget.remaining_amount < 0) return '超支';
                        return '0.0%';
                      })()}</span>
                    </div>
                  </div>
                </div>
                
                {/* 折扣 & 稅率設定卡片 */}
                <div className="p-4 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl text-white">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm opacity-90 mb-1">🏷️ 折扣 & 🧾 稅率</p>
                      <p className="text-2xl font-bold">
                        {activeBudget.discount_rate > 0 && activeBudget.discount_rate < 1
                          ? `${(activeBudget.discount_rate * 10).toFixed(1)} 折`
                          : '無折扣'
                        }
                        {' / '}
                        {`稅 ${((activeBudget.tax_rate ?? 0.05) * 100).toFixed(0)}%`}
                      </p>
                      <p className="text-xs opacity-75 mt-1">
                        {activeBudget.discount_rate > 0 && activeBudget.discount_rate < 1
                          ? `費用 ×${activeBudget.discount_rate} `
                          : ''
                        }
                        {`再加稅 ${((activeBudget.tax_rate ?? 0.05) * 100).toFixed(0)}%`}
                      </p>
                    </div>
                    <button
                      onClick={handleEditDiscount}
                      disabled={!canEdit}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${canEdit ? 'bg-white/20 hover:bg-white/30' : 'bg-white/10 opacity-50 cursor-not-allowed'}`}
                    >
                      修改
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleEditBudget}
                    disabled={!canEdit}
                    className={`py-2 text-white rounded-lg font-medium text-sm flex items-center justify-center space-x-1 ${canEdit ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-yellow-300 opacity-50 cursor-not-allowed'}`}
                  >
                    <span>+/-</span>
                    <span>預算</span>
                  </button>
                  <button
                    onClick={handleResetBudget}
                    disabled={!canEdit}
                    className={`py-2 text-white rounded-lg font-medium text-sm ${canEdit ? 'bg-red-500 hover:bg-red-600' : 'bg-red-300 opacity-50 cursor-not-allowed'}`}
                  >
                    🔄 歸零
                  </button>
                </div>
              </div>
            ) : isEditingDiscount ? (
              /* 折扣 & 稅率編輯表單 */
              <div className="space-y-4">
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-gray-600">
                    目前折扣: <span className="font-bold text-purple-700">
                      {activeBudget.discount_rate > 0 && activeBudget.discount_rate < 1 
                        ? `${(activeBudget.discount_rate * 10).toFixed(1)} 折` 
                        : '無折扣'}
                    </span>
                    {' / '}
                    稅率: <span className="font-bold text-purple-700">
                      {((activeBudget.tax_rate ?? 0.05) * 100).toFixed(0)}%
                    </span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🏷️ 折扣率
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={budgetForm.discount_rate}
                    onChange={(e) => setBudgetForm({ ...budgetForm, discount_rate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="例如: 0.8 表示 8 折"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {parseFloat(budgetForm.discount_rate) > 0 && parseFloat(budgetForm.discount_rate) < 1
                      ? `費用將乘以 ${budgetForm.discount_rate} (${(parseFloat(budgetForm.discount_rate) * 10).toFixed(1)} 折)`
                      : '留空表示不打折，輸入 0.8 表示 8 折'
                    }
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🧾 稅率
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={budgetForm.tax_rate}
                    onChange={(e) => setBudgetForm({ ...budgetForm, tax_rate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="例如: 0.05 表示 5%"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {parseFloat(budgetForm.tax_rate) > 0
                      ? `折扣後金額加稅 ${(parseFloat(budgetForm.tax_rate) * 100).toFixed(0)}%，即 ×${(1 + parseFloat(budgetForm.tax_rate)).toFixed(2)}`
                      : '免稅 0%；輸入 0.05 表示加收 5% 稅（新預算預設 5%）'
                    }
                  </p>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => setBudgetForm({ ...budgetForm, tax_rate: '0' })}
                      className="px-2 py-1 text-xs border rounded text-gray-700 hover:bg-gray-50"
                    >
                      免稅 0%
                    </button>
                    <button
                      type="button"
                      onClick={() => setBudgetForm({ ...budgetForm, tax_rate: '0.05' })}
                      className="px-2 py-1 text-xs border rounded text-gray-700 hover:bg-gray-50"
                    >
                      營業稅 5%
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveDiscount}
                    disabled={loading || !canEdit}
                    className="flex-1 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 font-medium disabled:opacity-50"
                  >
                    {loading ? '儲存中...' : '✓ 儲存折扣 & 稅率'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium"
                  >
                    取消
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleCreateBudget} className="space-y-4">
                {isEditingBudget && activeBudget && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-gray-600">目前預算: <span className="font-bold text-gray-900">${activeBudget.initial_amount.toLocaleString()}</span></p>
                    {budgetForm.initial_amount && parseFloat(budgetForm.initial_amount) !== 0 && (
                      <p className="text-sm text-gray-600 mt-1">
                        調整後: <span className="font-bold text-gray-900">${(activeBudget.initial_amount + parseFloat(budgetForm.initial_amount || 0)).toLocaleString()}</span>
                        <span className={`ml-2 ${parseFloat(budgetForm.initial_amount) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ({parseFloat(budgetForm.initial_amount) > 0 ? '+' : ''}{parseFloat(budgetForm.initial_amount).toLocaleString()})
                        </span>
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isEditingBudget ? '增減金額 (正數增加/負數減少)' : '預算金額'}
                  </label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    value={budgetForm.initial_amount}
                    onChange={(e) => setBudgetForm({ ...budgetForm, initial_amount: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder={isEditingBudget ? "例: +10000 或 -5000" : "例: 100000"}
                  />
                </div>
                
                {/* 新建預算時顯示折扣 & 稅率設定 */}
                {!isEditingBudget && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">🏷️ 折扣率</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        value={budgetForm.discount_rate}
                        onChange={(e) => setBudgetForm({ ...budgetForm, discount_rate: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="例如: 0.8 表示 8 折"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {parseFloat(budgetForm.discount_rate) > 0 && parseFloat(budgetForm.discount_rate) < 1
                          ? `上傳報表時自動乘以 ${budgetForm.discount_rate} (${(parseFloat(budgetForm.discount_rate) * 10).toFixed(1)} 折)`
                          : '留空表示不折扣，輸入 0.8 表示 8 折'
                        }
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">🧾 稅率</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        value={budgetForm.tax_rate}
                        onChange={(e) => setBudgetForm({ ...budgetForm, tax_rate: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="例如: 0.05 表示 5%"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {parseFloat(budgetForm.tax_rate) > 0
                          ? `折扣後金額加稅 ${(parseFloat(budgetForm.tax_rate) * 100).toFixed(0)}%`
                          : '免稅 0%；輸入 0.05 表示加收 5% 營業稅（新預算預設 5%）'
                        }
                      </p>
                      <div className="flex gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => setBudgetForm({ ...budgetForm, tax_rate: '0' })}
                          className="px-2 py-1 text-xs border rounded text-gray-700 hover:bg-gray-50"
                        >
                          免稅 0%
                        </button>
                        <button
                          type="button"
                          onClick={() => setBudgetForm({ ...budgetForm, tax_rate: '0.05' })}
                          className="px-2 py-1 text-xs border rounded text-gray-700 hover:bg-gray-50"
                        >
                          營業稅 5%
                        </button>
                      </div>
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">幣別</label>
                  <div className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700">
                    TWD - 新台幣
                  </div>
                </div>

                {/* 受訂單號和其他單號 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">📋 受訂單號（選填）</label>
                  <input
                    type="text"
                    value={budgetForm.order_number}
                    onChange={(e) => setBudgetForm({ ...budgetForm, order_number: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="輸入受訂單號"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">📋 其他單號（必填）</label>
                  <input
                    type="text"
                    required
                    value={budgetForm.other_number}
                    onChange={(e) => setBudgetForm({ ...budgetForm, other_number: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="輸入其他單號"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">📝 備註（選填）</label>
                  <textarea
                    rows="3"
                    value={budgetForm.remark}
                    onChange={(e) => setBudgetForm({ ...budgetForm, remark: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="可註明此筆款項的用途，例如人工服務費、代墊費用等"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={loading || !canEdit}
                    className="flex-1 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium disabled:opacity-50"
                  >
                    {loading ? (isEditingBudget ? '調整中...' : '設定中...') : (isEditingBudget ? '✓ 確認調整' : '✓ 確認設定')}
                  </button>
                  {isEditingBudget && (
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium"
                    >
                      取消
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>

          {/* 中間: 上傳報表 (步驟 2) */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-purple-500 rounded-lg">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">上傳報表</h2>
              </div>
              <button
                onClick={() => navigate('/report-analysis')}
                className="flex items-center space-x-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
              >
                <FlaskConical className="w-4 h-4" />
                <span>報表測試</span>
              </button>
            </div>

            {!activeBudget ? (
              <div className="text-center py-8 text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>請先完成預算設定</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging 
                      ? 'border-blue-500 bg-blue-50' 
                      : canEdit 
                        ? 'border-gray-300 hover:border-blue-500' 
                        : 'border-gray-200 bg-gray-50 opacity-60'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="fileInput"
                    onChange={handleFileChange}
                    accept=".xlsx,.xls"
                    multiple
                    className="hidden"
                    disabled={!canEdit}
                  />
                  <label htmlFor="fileInput" className={canEdit ? 'cursor-pointer' : 'cursor-not-allowed'}>
                    <Upload className={`w-12 h-12 mx-auto mb-2 transition-colors ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                    <p className="text-sm text-gray-600">
                      {!canEdit 
                        ? '僅限檢視，無法上傳'
                        : isDragging
                          ? '放開以上傳檔案'
                          : files.length > 0 
                            ? `已選擇 ${files.length} 個檔案` 
                            : '拖曳檔案至此，或點擊選擇 Excel 檔案（支援多選）'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">支援 XLSX 格式，可批量上傳</p>
                  </label>
                </div>

                {/* 顯示已選檔案列表 */}
                {files.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <p className="text-xs font-medium text-gray-500 mb-2">待上傳檔案：</p>
                    <div className="space-y-1">
                      {files.map((f, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 truncate">{f.name}</span>
                          <span className="text-gray-400 text-xs">{(f.size / 1024).toFixed(1)} KB</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 批量進度顯示 */}
                {(uploading || analyzing) && batchProgress.total > 0 && (
                  <div className="bg-blue-50 rounded-lg p-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-blue-700">{batchProgress.currentFile}</span>
                      <span className="text-blue-600">{batchProgress.current} / {batchProgress.total}</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleUploadAndAnalyze}
                  disabled={files.length === 0 || uploading || analyzing || !canEdit}
                  className="w-full py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 font-medium disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {uploading || analyzing ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>{uploading ? '批量上傳中...' : 'AI 批量分析中...'}</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>{files.length > 1 ? `批量上傳並分析 (${files.length} 檔)` : '上傳並自動分析'}</span>
                    </>
                  )}
                </button>

                {reports.length > 0 && (
                  <div className="pt-4 border-t">
                    <button
                      onClick={() => setShowHistoryModal(true)}
                      className="w-full py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-indigo-50 hover:text-indigo-700 font-medium text-sm transition-colors flex items-center justify-center space-x-2"
                    >
                      <FileText className="w-4 h-4" />
                      <span>歷史報表 ({reports.length})</span>
                    </button>
                  </div>
                )}

                <ReportHistoryModal
                  isOpen={showHistoryModal}
                  onClose={() => setShowHistoryModal(false)}
                  reports={reports}
                  onSelectReport={(reportId) => {
                    switchReport(reportId);
                    setShowHistoryModal(false);
                  }}
                  onClearReports={handleClearReports}
                  onDeleteReport={(deletedReportId) => {
                    // 從報表列表中移除已刪除的報表
                    setReports(prev => prev.filter(r => r.id !== deletedReportId));
                    // 如果正在查看的就是被刪除的報表，清空明細
                    if (selectedReport === deletedReportId) {
                      cancelPendingReportRequest();
                      setSelectedReport(null);
                      setExpenseItems([]);
                      setExpenseItemsReportId(null);
                      setSummarySource(null);
                      setCurrentStep(2);
                    }
                    // 重新載入預算資料（因為退款了）
                    checkCustomerStatus();
                  }}
                  onRetryReport={async (reportId) => {
                    await checkCustomerStatus(reportId);
                    setMessage({ type: 'success', text: '✅ 報表重新分析完成' });
                  }}
                  canEdit={canEdit}
                  loading={loading}
                />
              </div>
            )}
          </div>

          {/* 右側: 手動輸入報表 */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center space-x-2 mb-4">
              <div className="p-2 bg-indigo-500 rounded-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">手動輸入報表</h2>
            </div>

            {activeBudget ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">手動輸入報表（不透過 AI 分析）</h3>
                  <form onSubmit={handleCreateManualReport} className="space-y-3">
                    <YearMonthPicker
                      label="帳期起始日期"
                      value={manualReportForm.billing_start}
                      onChange={(value) => setManualReportForm({ ...manualReportForm, billing_start: value })}
                      mode="date"
                      required
                    />
                    <YearMonthPicker
                      label="帳期結束日期"
                      value={manualReportForm.billing_end}
                      onChange={(value) => setManualReportForm({ ...manualReportForm, billing_end: value })}
                      mode="date"
                      dayStrategy="last"
                      required
                    />
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">原始總金額（未稅）</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        value={manualReportForm.total_amount}
                        onChange={(e) => setManualReportForm({ ...manualReportForm, total_amount: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="例如 120000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">備註（選填）</label>
                      <textarea
                        rows="3"
                        value={manualReportForm.remark}
                        onChange={(e) => setManualReportForm({ ...manualReportForm, remark: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                        placeholder="可註明此筆手動扣款的用途"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={manualSubmitting || !canEdit}
                      className="w-full py-2.5 text-sm font-medium bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50"
                    >
                      {manualSubmitting ? '建立中...' : '建立手動報表'}
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>尚未設定預算</p>
              </div>
            )}
          </div>
        </div>

        {/* 步驟 3: 費用明細 (完整寬度) */}
        {currentStep === 3 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-indigo-500 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">費用明細分析</h2>
                  {currentReport && (
                    <div className="mt-1 flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-1.5 text-indigo-500" />
                      <span>報表年月區間：{formatBillingPeriod(currentReport)}</span>
                    </div>
                  )}
                </div>
              </div>

              {expenseItems.length > 0 && (
              <div className="flex space-x-3">
                <button
                  onClick={handleGenerateExcel}
                  disabled={loading}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>下載 Excel</span>
                </button>
                {/* 郵件發送功能暫時關閉 */}
                {/* <button
                  onClick={handleSendEmail}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
                >
                  <Mail className="w-4 h-4" />
                  <span>發送郵件</span>
                </button> */}
              </div>
              )}
            </div>

            {/* Loading 狀態 */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <span className="ml-3 text-gray-600">載入中...</span>
              </div>
            )}

            {/* 空狀態 */}
            {!loading && !itemsLoading && expenseItems.length === 0 && !isManualReport && (
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-2">此報表尚未有費用明細</p>
                <p className="text-sm text-gray-400">請點擊「重新分析」按鈕分析報表</p>
              </div>
            )}

            {!loading && !itemsLoading && expenseItems.length === 0 && isManualReport && (
              <div className="text-center py-10 text-gray-500">
                <BarChart3 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-600 mb-1">此為手動輸入報表</p>
                <p className="text-sm text-gray-400">不產生費用項目，僅顯示金額統計</p>
              </div>
            )}

            {/* 統計卡片 - 顯示折扣 & 稅額資訊 */}
            {!loading && !itemsLoading && shouldShowSummaryCards && (() => {
              const discountRate = summaryReport?.discount_rate || 0;
              const taxRateApplied = summaryReport?.tax_rate_applied || 0;
              const originalAmount = Math.round(summaryReport?.total_amount ?? 0);
              // 折扣後金額四捨五入到整數
              const discountedAmount = Math.round(summaryReport?.discounted_amount ?? originalAmount);
              const taxAmount = Math.round(summaryReport?.tax_amount ?? 0);
              const amountWithTax = Math.round(summaryReport?.amount_with_tax ?? discountedAmount);
              const hasTax = taxRateApplied > 0 && taxAmount > 0;
              
              return (
                <div key={`stats-${selectedReport}`} className={`grid ${hasTax ? 'grid-cols-4' : 'grid-cols-3'} gap-4 mb-6`}>
                  <div className="p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl text-white">
                    <p className="text-sm opacity-90 mb-1">原始金額(未稅)</p>
                    <p className="text-2xl font-bold">{formatCurrency(originalAmount)}</p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl text-white">
                    <p className="text-sm opacity-90 mb-1">🏷️ 折扣</p>
                    <p className="text-2xl font-bold">
                      {discountRate > 0 && discountRate < 1 
                        ? `${(discountRate * 10).toFixed(1)} 折` 
                        : '無'}
                    </p>
                    {discountRate > 0 && discountRate < 1 && (
                      <p className="text-xs opacity-75 mt-1">折後 {formatCurrency(discountedAmount)}</p>
                    )}
                  </div>
                  {hasTax && (
                    <div className="p-4 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl text-white">
                      <p className="text-sm opacity-90 mb-1">🧾 稅額 ({(taxRateApplied * 100).toFixed(0)}%)</p>
                      <p className="text-2xl font-bold">+{formatCurrency(taxAmount)}</p>
                    </div>
                  )}
                  <div className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl text-white">
                    <p className="text-sm opacity-90 mb-1">💰 實際扣款{hasTax ? '(含稅)' : ''}</p>
                    <p className="text-2xl font-bold">{formatCurrency(hasTax ? amountWithTax : discountedAmount)}</p>
                    {discountRate > 0 && discountRate < 1 && !hasTax && (
                      <p className="text-xs opacity-75 mt-1">折扣省 {formatCurrency(originalAmount - discountedAmount)}</p>
                    )}
                    {hasTax && (
                      <p className="text-xs opacity-75 mt-1">未稅折後 {formatCurrency(discountedAmount)}</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* 圖表 */}
            {!loading && expenseItems.length > 0 && (
            <div key={`charts-${selectedReport}`} className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">類別分佈</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={stats.categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percentage }) => {
                        // 只顯示比例 >= 3% 的標籤,避免重疊
                        const percent = parseFloat(percentage);
                        return percent >= 3 ? `${name} (${percentage}%)` : '';
                      }}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {stats.categoryData.map((entry, index) => (
                        <Cell key={`cell-${selectedReport}-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `${formatCurrency(value)} (${props.payload.percentage}%)`,
                        name
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4">類別金額</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.categoryData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#8b5cf6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            )}

            {/* 詳細表格 */}
            {!loading && expenseItems.length > 0 && (
            <div key={`table-${selectedReport}`}>
              <h3 className="text-lg font-semibold mb-4">費用項目明細</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">類別</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">服務名稱</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">筆數</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">描述</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">金額</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">折扣</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {expenseItems.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                            {item.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.service_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                          <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">
                            {item.quantity ? `${Math.floor(item.quantity)} 項` : '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                          {/* 移除數量顯示,只保留描述 */}
                          {item.description ? item.description.replace(/^[^(]+\(\d+\s*[項筆服務?]+\)\s*-?\s*/, '') || item.service_name : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(item.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {/* ai_confidence: 1.0=套用折扣, 0.0=不套用折扣 */}
                          {(item.ai_confidence || 0) >= 0.5 ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                              ✓ 套用
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                              ✗ 不套用
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
